import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Subscription } from 'rxjs';

export type InputType = 'text' | 'number' | 'tel';

export interface InputDialogData {
  tryUpdate: (input: string) => void;
  inputLabel: string;
  text: string;
  inputChecker: (input: string) => boolean;
  errorMessage: string;
  defaultInput: string;
  type: InputType;
  formatter?: (value: string) => string;
}

@Component({
  selector: 'app-input-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatFormField,
    MatInput,
    MatButton,
    MatDialogClose,
    MatLabel,
    MatSuffix,
    TranslatePipe,
    MatDialogActions,
    FormsModule,
    MatError,
    ReactiveFormsModule,
    MatIconButton,
    MatIcon,
  ],
  templateUrl: './input-dialog.component.html',
  styleUrl: './input-dialog.component.scss',
})
export class InputDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('input') inputElementRef!: ElementRef<HTMLInputElement>;

  tryUpdate;
  inputLabel;
  text;
  errorMessage = signal('');
  updateErrorMessage: () => void;
  inputControl: FormControl<string | null>;

  valueChangesSubscription: Subscription | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: InputDialogData,
  ) {
    this.tryUpdate = data.tryUpdate;
    this.inputLabel = data.inputLabel;
    this.text = data.text;
    const myValidator: ValidatorFn = (control: AbstractControl) => {
      return data.inputChecker(control.value) ? null : { customError: true };
    };
    this.inputControl = new FormControl(data.defaultInput, [myValidator]);
    this.updateErrorMessage = () => {
      if (this.inputControl.hasError('customError')) {
        this.errorMessage.set(data.errorMessage);
      } else {
        this.errorMessage.set('');
      }
    };

    // When a formatter is registered and the input value changes,
    // update the input control value without emitting an event.
    this.valueChangesSubscription = this.inputControl.valueChanges.subscribe(
      (currentValue) => {
        if (data.formatter) {
          this.inputControl.setValue(data.formatter(currentValue ?? ''), {
            emitEvent: false,
          });
          this.inputControl.updateValueAndValidity({ emitEvent: false });
        }
      },
    );
  }

  ngAfterViewInit(): void {
    // Pre-select the default value so typing immediately replaces it (e.g. "0" wouldn't become "080").
    this.inputElementRef.nativeElement.select();
  }

  getInputModeAttribute() {
    switch (this.data.type) {
      case 'number':
        return 'numeric';
      case 'tel':
        return 'tel';
      case 'text':
      default:
        return 'text';
    }
  }

  ngOnDestroy() {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }
}
